import cx from 'classnames';
import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import {
	map,
	isEmpty,
	indexOf,
} from 'lodash';
import {
	string as yup_string,
	number as yup_number,
	date as yup_date,
} from "yup";
import Input from 'components/forms/Input';
import { Calendar } from 'components/Icon.js';
import CmsContentRenderer from 'components/data/CmsContentRenderer.js';
import CmsContentRendered from "components/data/CmsContentRendered.js";
import RadioInput from 'components/forms/inputs/RadioInput.js';
import Panel from 'components/Panel.js';
import Select from 'components/forms/inputs/Select.js';
import { useAutoloadContext } from "context/AutoloadContext.js";
import {
	BIWEEKLY,
	MONTHLY,
	RECURRING,
	THRESHOLD,
	WEEKLY,
	sequenceGen,
} from 'utils/Constants.js';
import {
	AutoLoadCriteriaContext,
	BIWEEKLY_FREQUENCY,
	WEEKLY_MONTHLY_FREQUENCY,
} from 'components/account/purchases/AutoLoadCriteriaContext.js';

import { useLanguage } from "components/data/Language.js";
import PublicAppVars, { defaults } from 'utils/PublicAppVars.js';
import {
	centsToDisplay,
	ISODatePart,
	localizeWeekDay,
	makeOrdinal,
} from 'utils/FormatHelpers.js';

import CmsContentRenderedInline from 'components/data/CmsContentRenderedInline.js';
import { AUTO_LOAD_CRITERIA_TYPES } from 'server/api-types/WSAutoloadCriteria.js';

import * as historyFilters from 'components/HistoryFilters.module.css';
import * as style from './AutoloadBalance.module.css';
import CmsContentList from 'components/data/CmsContentList';

const cms = {
	balanceWhenThreshold: 'miscText.autoload-balance-when-threshold',
	balanceWhenThresholdSelect: 'miscText.autoload-balance-when-threshold-select',
	balanceWhenDate: 'miscText.autoload-balance-when-date',
	balanceWhenDateSelect: 'miscText.autoload-balance-when-date-select',
	balanceWhenInterval: 'miscText.autoload-balance-when-interval',
	balanceWhenIntervalSelect: 'miscText.autoload-balance-when-interval-select',
	selectWeekly: 'miscText.autoload-balance-when-interval-select-weekly',
	selectBiweekly: 'miscText.autoload-balance-when-interval-select-biweekly',
	selectMonthly: 'miscText.autoload-balance-when-interval-select-monthly',
	balanceWhenRepeatOn: 'miscText.autoload-balance-when-interval-repeat-on',
	balanceWhenStartOn: 'miscText.autoload-balance-when-interval-start-on',
	monthDay: 'miscText.autoload-balance-when-interval-month-day',
	mon: 'miscText.autoload-balance-when-interval-weekly-mon',
	tue: 'miscText.autoload-balance-when-interval-weekly-tue',
	wed: 'miscText.autoload-balance-when-interval-weekly-wed',
	thu: 'miscText.autoload-balance-when-interval-weekly-thu',
	fri: 'miscText.autoload-balance-when-interval-weekly-fri',
	sat: 'miscText.autoload-balance-when-interval-weekly-sat',
	sun: 'miscText.autoload-balance-when-interval-weekly-sun',
	monthDescription: 'miscText.autoload-balance-when-interval-month-description',
	autoloadOtherValue: 'miscText.autoload-select-other-option',
	autoloadOtherValueLabel: 'miscText.autoload-select-other-option-input-label',
};

const AUTOLOAD_OTHER_VALUE = "Other";

const optionsRecurrence = [
	{
		label: <CmsContentRendered.Span contentKey={cms.selectWeekly} fallbackValue="Week" />,
		value: WEEKLY,
	},
	{
		label: <CmsContentRendered.Span contentKey={cms.selectBiweekly} fallbackValue="2 Weeks" />,
		value: BIWEEKLY,
	},
	{
		label: <CmsContentRendered.Span contentKey={cms.selectMonthly} fallbackValue="Month" />,
		value: MONTHLY,
	},
];

const optionsDayMonthly = map(sequenceGen(1, 31), day => ({
	label: <CmsContentRenderedInline contentKey={cms.monthDay} fallbackValue={`${makeOrdinal(day)} day of the month`}
		variables={{ day: makeOrdinal(day) }} />,
	value: day.toString(),
}));


export const autoloadFrequencyValidations = (recurring) => {
	let validation;

	if (recurring === RECURRING) {
		validation = {
			recurrence: yup_string().required('Please select a recurrence.'),
			day: yup_string().required('Please select a day.'),
			frequency: yup_string(),
		};
	} else {
		validation = {
			reloadThreshold: yup_number().required('Please select a value.'),
		};
	}

	return {
		type: yup_string().required('Please select a type.'),
		startDate: yup_date(),
		...validation,
	};
};

const getDefaultThresholdValue = (thresholdValue) => {
	const index = indexOf(PublicAppVars.AUTOMATIC_RELOAD_BALANCE_THRESHOLD, thresholdValue);
	return PublicAppVars.AUTOMATIC_RELOAD_BALANCE_THRESHOLD[ index >= 0 ? index : 0 ];
};

const getDefaultWeekBased = (wsAutoloadSubscriptionInfo) => {
	if (!isEmpty(wsAutoloadSubscriptionInfo) && wsAutoloadSubscriptionInfo?.criteria?.recurrence === MONTHLY) {
		return false;
	}
	return true;
};

const getDefaultRecurrence = (wsAutoloadSubscriptionInfo) => {
	if (!isEmpty(wsAutoloadSubscriptionInfo) && wsAutoloadSubscriptionInfo?.criteria?.type === RECURRING) {
		if (wsAutoloadSubscriptionInfo?.criteria?.recurrence === WEEKLY) {
			if (wsAutoloadSubscriptionInfo?.criteria?.frequency === WEEKLY_MONTHLY_FREQUENCY) {
				return WEEKLY;
			} else {
				return BIWEEKLY;
			}
		}
		return MONTHLY;
	}
	return WEEKLY;
};

const getDefaultDay = (wsAutoloadSubscriptionInfo) => {
	if (!isEmpty(wsAutoloadSubscriptionInfo)) {
		return wsAutoloadSubscriptionInfo?.criteria?.day;
	}
	return null;
};

const getDefaultStartDay = (wsAutoloadSubscriptionInfo) => {
	if (!isEmpty(wsAutoloadSubscriptionInfo)) {
		return wsAutoloadSubscriptionInfo?.startDate.split("T")[ 0 ];
	}
	return null;
};

const AutoloadFrequency = ({
	updateFrequency,
	formHelper: parentFormHelper,
}) => {
	const today = ISODatePart(new Date());
	const language = useLanguage();
	const location = useLocation();
	const { wsAutoloadSubscriptionInfo } = location?.state ?? {};

	const [ recurring, setRecurring ] = useState(false);
	const [ isWeekBased, setWeekBased ] = useState(getDefaultWeekBased(wsAutoloadSubscriptionInfo));

	const [ threshold, setThreshold ] = useState(getDefaultThresholdValue(wsAutoloadSubscriptionInfo?.criteria?.thresholdValue));
	const [ recurrence, setRecurrence ] = useState(getDefaultRecurrence(wsAutoloadSubscriptionInfo));
	const [ day, setDay ] = useState(getDefaultDay(wsAutoloadSubscriptionInfo));
	const [ start, setStart ] = useState(getDefaultStartDay(wsAutoloadSubscriptionInfo));
	const [ originalStart, setOriginalStart ] = useState(getDefaultStartDay(wsAutoloadSubscriptionInfo));
	const { autoloads } = useAutoloadContext();
	const { currentSubscriptionInfo } = autoloads ?? {};

	const {
		autoLoadCriteriaType,
		setAutoLoadCriteriaType,
	} = useContext(AutoLoadCriteriaContext);

	useEffect(() => {
		const criteriaProps = recurring
			? {
				recurrence: recurrence,
				day,
				frequency: recurrence === BIWEEKLY ? BIWEEKLY_FREQUENCY : WEEKLY_MONTHLY_FREQUENCY,
			}
			: {	thresholdValue: threshold };

		const type = recurring
			? AUTO_LOAD_CRITERIA_TYPES.recurring
			: autoLoadCriteriaType === AUTO_LOAD_CRITERIA_TYPES.thresholdOther
				? AUTO_LOAD_CRITERIA_TYPES.thresholdOther
				: AUTO_LOAD_CRITERIA_TYPES.threshold;

		updateFrequency({
			...criteriaProps,
			type,
			startDate: start,
			originalStart,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		recurring,
		threshold,
		recurrence,
		day,
		start,
		originalStart,
	]);

	useEffect(() => {
		if (!isEmpty(currentSubscriptionInfo)) {
			if (currentSubscriptionInfo?.criteria?.type === THRESHOLD) {
				setRecurring(false);
			} else {
				setRecurring(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ currentSubscriptionInfo ]);

	const optionsDayWeekly = map(
		sequenceGen(0, 7),
		(day) => {
			return ({
				label: localizeWeekDay(language, day),
				value: localizeWeekDay(defaults.LANGUAGE, day, { weekday: 'short' }).toUpperCase(),
			});
		});

	const onRecurrenceChange = ({ value }) => {
		setWeekBased(value === BIWEEKLY || value === WEEKLY);
		setRecurrence(value);
		setDay(null);
	};

	const getDay = (value) => isWeekBased
		? optionsDayWeekly.filter(opt => opt.value === value)
		: optionsDayMonthly.filter(opt => opt.value === value);

	return (
		<Panel overrideClass={style.frequencyWrapper}>
			<CmsContentList list={Object.values(cms)}>{() => <>
				<input type="hidden" name="type" value={recurring ? RECURRING : THRESHOLD} />
				<div className={cx(!recurring ? style.selected : '', style.frequencyOption)}>
					<RadioInput
						name="frequencyType"
						additionalClasses={style.frequencyRadio}
						label={<CmsContentRenderer.Span contentKey={cms.balanceWhenThreshold}
							fallbackValue="When my balance falls below a specified amount" />}
						checked={!recurring}
						onChange={({ target }) => target.checked && setRecurring(false)}
					/>

					{!recurring &&
					<div className={cx(style.frequencyOptionContent, style.thresholdSelection)}>
						<div>
							<label htmlFor={"reloadThreshold-selector"}>
								<CmsContentRenderer.Span contentKey={cms.balanceWhenThresholdSelect}
									fallbackValue="When my balance falls below " />
							</label>
						</div>
						<div>
							<Select
								name={"reloadThreshold"}
								hideLabel={true}
								onChange={({ value }) => {
									if (value === AUTOLOAD_OTHER_VALUE) {
										setAutoLoadCriteriaType(AUTO_LOAD_CRITERIA_TYPES.thresholdOther);
									}
									else {
										setAutoLoadCriteriaType(AUTO_LOAD_CRITERIA_TYPES.threshold);
										setThreshold(value);
									}
								}}
								options={map(PublicAppVars.AUTOMATIC_RELOAD_BALANCE_THRESHOLD, option => ({
									value: option,
									label: option === AUTOLOAD_OTHER_VALUE
										? <CmsContentRenderer.Span contentKey={cms.autoloadOtherValue} fallbackValue="Other" />
										: centsToDisplay(option),
								}))}
								defaultValue={threshold}
								error={parentFormHelper.getFieldError("reloadThreshold")}
							/>
						</div>
					</div>
					}

					{autoLoadCriteriaType === AUTO_LOAD_CRITERIA_TYPES.thresholdOther &&
					<Input
						label={
							<CmsContentRenderedInline
								contentKey={cms.autoloadOtherValueLabel}
								fallbackValue={`Enter amount (max ${centsToDisplay(PublicAppVars.AUTOMATIC_RELOAD_BALANCE_OTHER_MAX)})`}
								variables={{ value: centsToDisplay(PublicAppVars.AUTOMATIC_RELOAD_BALANCE_OTHER_MAX) }}
							/>
						}
						name="reloadThresholdOther"
						type="text"
						error={parentFormHelper.getFieldError("reloadThresholdOther")}
						placeholder={centsToDisplay(threshold)}
						onBlur={({ target }) => setThreshold(target.value)}
					/>
					}
				</div>

				<div className={cx(recurring ? style.selected : '', style.frequencyOption)}>
					<RadioInput
						name="frequencyType"
						additionalClasses={style.frequencyRadio}
						label={<CmsContentRenderer.Span contentKey={cms.balanceWhenInterval}
							fallbackValue="At a recurring interval" />}
						checked={recurring}
						onChange={({ target }) => target.checked && setRecurring(true)}
					/>

					{recurring &&
					<div className={style.frequencyOptionContent}>
						<div className={style.frequencySelectionGroup}>
							<Select
								name="recurrence"
								overrideClass={cx(style.frequencySelection)}
								onChange={onRecurrenceChange}
								options={optionsRecurrence}
								defaultValue={recurrence}
								error={parentFormHelper.getFieldError("recurrence")}
								label={<CmsContentRenderer.Span contentKey={cms.balanceWhenIntervalSelect}
									fallbackValue="Repeat every" />} />

							<Select
								name="day"
								overrideClass={cx(style.frequencySelection)}
								value={getDay(day)}
								onChange={({ value }) => setDay(value)}
								options={isWeekBased ? optionsDayWeekly : optionsDayMonthly}
								defaultValue={day}
								error={parentFormHelper.getFieldError("day")}
								label={<CmsContentRenderer.Span contentKey={cms.balanceWhenRepeatOn}
									fallbackValue="Repeat on" />} />

							<div className={historyFilters.calendarInputWrapper}>
								<Input
									label={<CmsContentRenderer.Span contentKey={cms.balanceWhenStartOn}
										fallbackValue="Starting on" />}
									name="startDateTime"
									data-qa="allDatesOption"
									type="date"
									error={parentFormHelper.getFieldError("startDateTime")}
									min={today}
									defaultValue={start}
									aria-label={
										<CmsContentRenderer.Span
											contentKey={cms.balanceWhenStartOn}
											fallbackValue="Starting on" />
									}
									onChange={(e) => setStart(e.target.value)}
									overrideClass={historyFilters.calendarInput}
								/>
								<Calendar
									overrideClass={cx(historyFilters.calendarIcon, historyFilters.dateRange, style.calendarIconOverride)}
									aria-hidden="true"
									data-qa="calendar-icon"
								/>
							</div>

						</div>
						{recurrence === MONTHLY &&
						 	<CmsContentRenderer.Div
						 		contentKey={cms.monthDescription}
						 		fallbackValue="Autoload will occur on the last day of the month for months with fewer than 31 days."
						 	/>
						}
					</div>
					}
				</div>
			</>}</CmsContentList>
		</Panel>
	);
};

export default AutoloadFrequency;
